import React, { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import EntitiesList from './EntitiesList'
import { Sort, EntityType } from 'util/constants'
import EntitiesSort from './EntitiesSort'
import IssuesListSelector from './IssuesListSelector'
import ImpactsListSelector from './ImpactsListSelector'
import SolutionsListSelector from './SolutionsListSelector'
import { setDocumentTitle } from 'util/pageTitles'
import { capitalizeFirstLetter } from 'util/textUtils'
import FeedbackListSelector from './FeedbackListSelector'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import ScrollableTabsList from 'ui/ScrollableTabsList'
import ScrollHideContainer from 'ui/ScrollHideContainer'
import AddFeedback from 'components/Feedback/AddFeedback'

interface FeedProps {
  username?: string
  actorId?: number
  entityTypes?: EntityType[]
  hideTabList?: boolean
  isActorPage?: boolean
}

const Feed: React.FC<FeedProps> = ({ username, actorId, entityTypes, hideTabList = false, isActorPage = false }) => {
  const { entityType: paramEntityType, sort: paramSort = 'top' } = useParams<{
    entityType: EntityType | ''
    sort?: Sort
  }>()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const sort = (searchParams.get('sort') as Sort) || paramSort
  const selectedTab = paramEntityType || 'issues'
  const [isLoading, setIsLoading] = useState(false)  
  const [error, setError] = useState<string | null>(null)
  const [isAddFeedbackOpen, setIsAddFeedbackOpen] = useState(false)

  // Function to map plural to singular EntityType
    const getEntityTypeForApi = (type: EntityType | null): EntityType | null => {
    switch (type) {
      case 'issues':
      case 'issue':
        return 'issue'
      case 'impacts':
      case 'impact':
        return 'impact'
      case 'solutions':
      case 'solution':
        return 'solution'
      case 'comments':
      case 'comment':
        return 'comment'
      case 'user-feedback':
        return 'feedback'
      case 'assessment':
        return 'assessment'
      default:
        return null
    }
  }

  const selectedEntityType = paramEntityType || 'issues'
  const currentEntityType = getEntityTypeForApi(selectedEntityType)

  useEffect(() => {
    if (paramEntityType && !paramEntityType.includes(currentEntityType as EntityType)) {
      setError('404: Not a supported entity type or sort option.')
    } else if (sort && !sort.match(/^top$|^bottom$|^old$|^new$/)) {
      setError('404: Not a supported sort option.')
    } else if (currentEntityType === 'issue' && sort !== 'top' && sort !== 'bottom' && sort !== 'old') {
      setDocumentTitle('Societea - Spill the tea on social issues')
    } else if (currentEntityType === 'issue') {
      setDocumentTitle('Feed - Issues - ' + capitalizeFirstLetter(sort))
    } else if (currentEntityType === 'feedback') {
      setDocumentTitle('User Feedback - ' + capitalizeFirstLetter(sort))
    } else {
      setDocumentTitle(`Feed - ${capitalizeFirstLetter(currentEntityType as EntityType)}s - ${capitalizeFirstLetter(sort)}`)
    }
  }, [currentEntityType, sort, paramEntityType])


  const handleSortChange = (newSort: Sort) => {
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('sort', newSort)
    if (username) {
      navigate(`${location.pathname}?${newSearchParams.toString()}`)
    } else {
      navigate(
        `${selectedEntityType === 'issues' ? '/' : `/${selectedEntityType}`}/${newSort}?${newSearchParams.toString()}`
      )
    }
  }

  const handleLoading = (isLoading: boolean) => {
    setIsLoading(isLoading)
  }

  const renderEntitiesList = (type: string) => (
    <>
      {username ? (
        <ScrollHideContainer 
          className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
          hideOffset={115}
          threshold={114}
        >
          <div className="flex gap-1 items-center py-1 px-2">
            <EntitiesSort entityType={currentEntityType as EntityType} sort={sort} onSortChange={handleSortChange} />
          </div>
        </ScrollHideContainer>
      ) : (
        <>
        <ScrollHideContainer 
          className={`sticky z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main`}
          hideOffset={type === 'user-feedback' ? 57 : 115}
          threshold={type === 'user-feedback' ? 56 : 114}
        >
          <div className="flex gap-1 items-center py-1 px-2">
            {type === 'issues' && <IssuesListSelector sort={sort} searchParams={searchParams} />}
            {type === 'impacts' && <ImpactsListSelector sort={sort} searchParams={searchParams} />}
            {type === 'solutions' && <SolutionsListSelector sort={sort} searchParams={searchParams} />}
            {type === 'user-feedback' && (
              <>
                <FeedbackListSelector 
                  sort={sort} 
                  searchParams={searchParams} 
                  onAddFeedbackClick={() => setIsAddFeedbackOpen(true)}
              />
              </>
            )}
          </div>
        </ScrollHideContainer>
              <AddFeedback
                open={isAddFeedbackOpen}
                  onClose={() => setIsAddFeedbackOpen(false)}
                />
        </>
      )}
      <EntitiesList
        entityTypes={entityTypes || [currentEntityType as EntityType]}
        sort={sort as Sort}
        searchParams={searchParams}
        username={username}
        actor={actorId}
        isLoading={handleLoading}
        isActorPage={isActorPage}
      />
      {isLoading && (
        <LoadingSkeleton count={5} />
      )}
    </>
  )
//REMOVED: already have NavLink on scrollable tabs so this causes double navigation
  /*const handleTabChange = (newTab: string) => {
    navigate(`/${newTab}/${sort}${location.search}`);
  };*/

  const tabsData = [
    {
      label: 'Issues',
      route: `/issues/${sort}`,
      value: 'issues',
    },
    {
      label: 'Impacts',
      route: `/impacts/${sort}`,
      value: 'impacts',
    },
    {
      label: 'Solutions',
      route: `/solutions/${sort}`,
      value: 'solutions',
    },
  ];

  if (error) {
    return <NotFoundPage />
  }

  if (hideTabList || selectedEntityType === 'user-feedback') {
    return renderEntitiesList(selectedEntityType)
  }

  return (
    <div className="py-0 relative">
      {!hideTabList && (
        <ScrollHideContainer 
          className="sticky z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
          hideOffset={57}
          threshold={56}
        >
          <div className="p-2">
            <ScrollableTabsList
              tabs={tabsData}
              selectedTab={selectedTab}
              onTabChange={() => {}}
              pageTitle="Feed"
            />
          </div>
        </ScrollHideContainer>
      )}
      <div className="relative gap-2">
        {(selectedEntityType === 'issues' || !paramEntityType) && renderEntitiesList('issues')}
        {selectedEntityType === 'impacts' && renderEntitiesList('impacts')}
        {selectedEntityType === 'solutions' && renderEntitiesList('solutions')}
      </div>
    </div>
  )
}

export default Feed
